import { useState, useMemo } from "react";
import { useSession } from "../context/SessionContext";
import { useOrderDraft } from "../context/OrderDraftContext";
import { usePatientForm } from "./usePatientForm";
import { useApi } from "./useApi";
import { useDownloadOpme, useDownloadGuiaInternacao, useDownloadResumo } from "./usePdfDownload";
import { saveService } from "../services/save";
import { chatService } from "../services/chat";
import { opmeTemplateFor } from "../lib/pdf";
import { formatSurgeryRequest } from "../lib/formatters";
import { transformDateAndHour } from "../lib/formatters";
import { ItemRecurso, ReferenciaSelecionadaPayload } from "../types/resources.types";
import { SavePayload, SaveResponse } from "../types/save.types";
import { SUCCESS_MESSAGES } from "../i18n/success.pt-BR";

export const usePdfActions = () => {
  const context = useOrderDraft();
  const session = useSession();
  const patientForm = usePatientForm(); // Consome os dados do formulário (CPF, carteirinha, etc.)

  // Controle de Modais Compartilhados
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [showOpmeSelection, setShowOpmeSelection] = useState(false);
  const [showReferencesModal, setShowReferencesModal] = useState(false);
  const [referencesModalInitialStep, setReferencesModalInitialStep] = useState<"prompt" | "validacao">("prompt");

  // Fluxo da "Descrição do paciente" resumida — etapa entre "Baixar guia de
  // internação" e o seletor de template (HealthPlanPicker).
  const [showDescricaoResumidaModal, setShowDescricaoResumidaModal] = useState(false);
  const [resumidaSuggestion, setResumidaSuggestion] = useState<string | null>(null);
  const [generatingResumida, setGeneratingResumida] = useState(false);
  const [resumidaError, setResumidaError] = useState(false);
  const [guiaResumida, setGuiaResumida] = useState<string | null>(null);

  // Hooks de download nativos
  const downloadOpme = useDownloadOpme();
  const downloadInternacao = useDownloadGuiaInternacao();
  const downloadResumo = useDownloadResumo();
  const { request, apiFetch } = useApi<SaveResponse>();

  const EMAIL = session.metadata.email;

  const patientNameSlug = context.surgeryRequestData.patientName.split(" ").join("_").toLowerCase();

  // Mapeia os itens negados para o modal de referências (usado na DownloadPage)
  const itensSelecionados: ItemRecurso[] = useMemo(() => {
    const itens: ItemRecurso[] = [];
    const procedimentosArray = context.selectedButtonCheckKits === "escolha-livre" ? context.checkedItems : context.checkedKit;
    const opmesArray = context.selectedButtonOpmeKits === "escolha-livre" ? context.opmeItems : context.opmeKit;

    (procedimentosArray || []).forEach((p) => {
      if (p.code) itens.push({ id: String(p.code), descricao: p.description, tipo: "CODIGO", status: "NEGADO" });
    });

    (opmesArray || []).forEach((o) => {
      if (o[0]) itens.push({
        id: `MAT-${String(o[0]).toUpperCase().replace(/\s+/g, "-").slice(0, 30)}`,
        descricao: o[1] ? `${o[0]} (qtd: ${o[1]})` : String(o[0]),
        tipo: "MATERIAL",
        status: "NEGADO",
      });
    });
    return itens;
  }, [context.selectedButtonCheckKits, context.checkedItems, context.checkedKit, context.selectedButtonOpmeKits, context.opmeItems, context.opmeKit]);

  // Função única de persistência (Auto-salvamento unificado)
  const saveState = async (extra?: Partial<SavePayload>): Promise<boolean> => {
    const payload: SavePayload = {
      timestamp: context.timestamp,
      hospital: context.surgeryRequestData.hospital,
      resumoOPME: context.surgeryRequestData.opmeSummary,
      pacient_name: context.surgeryRequestData.patientName,
      dataDesejada: transformDateAndHour(context.surgeryRequestData.desiredDateTime),
      pedido_cirurgia: formatSurgeryRequest(context.surgeryRequestData),
      cids: context.surgeryRequestData.cids,
      dataEmissao: context.surgeryRequestData.issueDate,
      // Se os dados do formulário existirem, envia-os também
      convenio: patientForm.convenio,
      cpf: patientForm.cpf,
      dataNasc: patientForm.birthDate,
      numCarteirinha: patientForm.documentNumber,
      valCarteirinha: patientForm.documentValidity,
      sexo: patientForm.sexo,
      ...extra,
    };

    const result = await request(() => saveService.post(apiFetch, payload), {
      successMessage: SUCCESS_MESSAGES.orderDataSaved,
    });
    return result?.status === "success";
  };

  // 2. POST /chat/descricao-resumida a partir da "Descrição do paciente"
  //    completa (que já está no pedido). Stateless, ~3-5s.
  const generateDescricaoResumida = async () => {
    setGeneratingResumida(true);
    setResumidaError(false);
    setResumidaSuggestion(null);
    try {
      const { descricao_resumida } = await chatService.descricaoResumida(apiFetch, {
        descricao_completa: context.surgeryRequestData.patientDescription,
        patient_name: context.surgeryRequestData.patientName,
      });
      setResumidaSuggestion(descricao_resumida);
    } catch {
      setResumidaError(true);
    } finally {
      setGeneratingResumida(false);
    }
  };

  // 1. Abre a etapa da descrição resumida. Reaproveita a versão já confirmada
  //    (nesta ou noutra tela) quando a descrição completa não mudou; senão,
  //    dispara a geração. O seletor de template (HealthPlanPicker) só abre depois
  //    que o médico confirma aqui.
  const openGuiaInternacaoFlow = () => {
    setResumidaError(false);
    setShowDescricaoResumidaModal(true);

    const completa = context.surgeryRequestData.patientDescription;
    const cache = context.guiaResumidaCache;
    if (cache && cache.completa === completa && cache.resumida.trim()) {
      setResumidaSuggestion(cache.resumida);
      setGuiaResumida(cache.resumida);
      setGeneratingResumida(false);
    } else {
      setResumidaSuggestion(null);
      generateDescricaoResumida();
    }
  };

  const closeGuiaInternacaoFlow = () => setShowDescricaoResumidaModal(false);

  // Entrada do fluxo de OPME (abre o seletor de plano) — mesmo gate.
  const openOpmeSelection = () => {
    setShowOpmeSelection(true);
  };

  // Entrada do fluxo de resumo com referências — mesmo gate. Reabrir refs já
  // aprovadas (onViewRefs) não passa por aqui: aquilo é acesso a conteúdo já
  // gerado, não geração nova.
  const openResumoFlow = () => {
    setReferencesModalInitialStep("prompt");
    setShowReferencesModal(true);
  };

  // 3. Médico confirmou (com ou sem edição) → guarda o texto (local + cache
  //    compartilhado entre telas) e libera o seletor de template. O save
  //    acontece dentro de handleDownloadGuiaInternacao.
  const confirmDescricaoResumida = (descricaoResumida: string) => {
    setGuiaResumida(descricaoResumida);
    context.setGuiaResumidaCache({
      resumida: descricaoResumida,
      completa: context.surgeryRequestData.patientDescription,
    });
    setShowDescricaoResumidaModal(false);
    setShowPlanSelection(true);
  };

  const handleDownloadGuiaInternacao = async (plan: string, reportType: string = "_pedido_de_cirurgia") => {
    const descricaoCompleta = context.surgeryRequestData.patientDescription;
    const cache = context.guiaResumidaCache;
    const descricaoResumida =
      guiaResumida ??
      (cache && cache.completa === descricaoCompleta ? cache.resumida : "");

    // 4. Persiste as duas versões junto com o resto do estado e baixa o PDF.
    if (!(await saveState({ descricao_completa: descricaoCompleta, descricao_resumida: descricaoResumida }))) return;

    await downloadInternacao.download(
      {
        email: EMAIL,
        pedido_de_cirurgia: formatSurgeryRequest(context.surgeryRequestData),
        template_type: plan,
        doctorName: session.metadata.nome,
        CRM: session.metadata.crm,
        desiredDate: transformDateAndHour(context.surgeryRequestData.desiredDateTime),
        documentNumber: patientForm.documentNumber,
        documentValidity: patientForm.documentValidity,
        descricao_resumida: descricaoResumida,
      },
      `${patientNameSlug}${reportType}.pdf`
    );
  };

  const handleDownloadOPME = async (plan: string, reportType: string = "_opme") => {
    if (!(await saveState())) return;

    const opme_data = (context.selectedButtonOpmeKits === "escolha-livre" ? context.opmeItems : context.opmeKit)
      .map((opme) => [opme[0], opme[1]] as [string, string]);

    await downloadOpme.download(
      {
        email: EMAIL,
        template_type: opmeTemplateFor(plan),
        opme_data,
        pacientName: context.surgeryRequestData.patientName,
        doctorName: session.metadata.nome,
        documentNumber: patientForm.documentNumber,
        justificativa_tecnica: context.surgeryRequestData.opmeSummary,
        observacoesOpme: context.observacoes,
        companies: context.selectedCompanies,
      },
      `${patientNameSlug}${reportType}.pdf`
    );
  };

  const handleFinalDownloadResumo = async (referencias?: ReferenciaSelecionadaPayload[], reportType: string = "_resumo") => {
    setShowReferencesModal(false);
    context.setLastApprovedRefs(referencias && referencias.length > 0 ? referencias : []);
    if (!(await saveState())) return;

    const materiais_opme = (context.selectedButtonOpmeKits === "escolha-livre" ? context.opmeItems : context.opmeKit)
      .map((opme) => `${opme[0]} x${opme[1]}`);

    await downloadResumo.download(
      {
        email: EMAIL,
        pedido_de_cirurgia: formatSurgeryRequest(context.surgeryRequestData),
        timestamp: context.timestamp,
        materiais_opme,
        desiredDate: transformDateAndHour(context.surgeryRequestData.desiredDateTime),
        justificativa_tecnica: context.surgeryRequestData.opmeSummary,
        observacoesOpme: context.observacoes,
        referencias,
        companies: context.selectedCompanies,
      },
      `${patientNameSlug}${reportType}.pdf`
    );
  };

  return {
    showPlanSelection, setShowPlanSelection,
    showOpmeSelection, setShowOpmeSelection,
    showReferencesModal, setShowReferencesModal,
    referencesModalInitialStep, setReferencesModalInitialStep,
    itensSelecionados,
    downloadInternacao, downloadOpme, downloadResumo,
    saveState, handleDownloadGuiaInternacao, handleDownloadOPME,
    handleFinalDownloadResumo,
    // Fluxo da descrição resumida da guia de internação
    showDescricaoResumidaModal, resumidaSuggestion, generatingResumida, resumidaError,
    openGuiaInternacaoFlow, closeGuiaInternacaoFlow,
    generateDescricaoResumida, confirmDescricaoResumida,
    openOpmeSelection, openResumoFlow,
  };
};